import { Box } from "@mui/material";
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";

import NothingHereYet from "./NothingHereYet";
import { HyroLoader } from "@hyro-components";
// eslint-disable-next-line no-duplicate-imports
import { HyroTabs } from "@hyro-components";
import InsightsBase from "../components/widgets/insights/InsightsBase";
import { INSIGHTS_LOADER_TEXT } from "../components/widgets/insights/InsightsConfig.ts";
import InsightsTab from "../components/widgets/insights/InsightsTab";
import SigmaDashboard from "../components/widgets/insights/SigmaDashboard";
import { useEffectAsync } from "@hooks";
import { get } from "../services/api";
import useAppStore from "../stores/appStore";
import {
  assistantIdSelector,
  isLoggedInToLookerSelector,
  setIsLoggedInToLookerSelector
} from "../stores/selectors/appSelectors";
import { useFlags } from "launchdarkly-react-client-sdk";

const OVERVIEW = "Overview";
const CALL_CENTER = "Call Center";

const getTabsSpec = (dashboards, assistantId) =>
  dashboards?.sort((a, b) => {
    if (a.dashboardName === OVERVIEW && b.dashboardName !== OVERVIEW) {
      return -1;
    }

    if (a.dashboardName !== OVERVIEW && b.dashboardName === OVERVIEW) {
      return 1;
    }
    return 0;

  })?.map(dashboard => ({
    label: dashboard.dashboardName,
    value: dashboard.dashboardName,
    content: dashboard.sigmaKey
      ? <SigmaDashboard assistantId={assistantId} dashboardKey={dashboard.sigmaKey} />
      : <InsightsTab {...dashboard} />
  }));

const AssistantInsights = () => {
  const [searchParams] = useSearchParams();
  const selectedAssistantId = useAppStore(assistantIdSelector);
  const isLoggedInToLooker = useAppStore(isLoggedInToLookerSelector);
  const setIsLoggedInToLooker = useAppStore(setIsLoggedInToLookerSelector);
  const { displayCallCenterDashboard } = useFlags();
  const [skillsDashboards, setSkillsDashboards] = useState(null);

  const filterDashboards = (dashboards) => {
    if (!dashboards) return [];
    if (!displayCallCenterDashboard) {
      return dashboards.filter(dashboard => dashboard.dashboardName !== CALL_CENTER);
    }
    return dashboards;
  };

  // eslint-disable-next-line consistent-return
  useEffectAsync(async () => {
    await setIsLoggedInToLooker(false);
    let dashboards;

    if (selectedAssistantId) {
      try {
        dashboards = await get({ endpoint: `assistants/${selectedAssistantId}/insights/dashboards` });
        setSkillsDashboards(dashboards);
      } catch (err) {
        if (err.response?.status === 404) {
          setSkillsDashboards([]);
        }
      }
    }

    const lookerDashboards = dashboards?.filter(dashboard => dashboard.embeddedDashboardData) ?? [];

    if (lookerDashboards.length > 0) {
      // find the min session length, after it expires we'll refresh it
      const sessionLength = Math.min(
        ...lookerDashboards.map(
          dashboard => dashboard.embeddedDashboardData.sessionLength
        )
      );

      const interval = setInterval(async () => {
        if (!selectedAssistantId) return;
        const dashboards = await get({ endpoint: `assistants/${selectedAssistantId}/insights/dashboards` });
        setSkillsDashboards(dashboards);
        setIsLoggedInToLooker(false);
      }, (sessionLength - 60) * 1000);

      return () => clearInterval(interval);
    }
  }, [selectedAssistantId]);

  const renderInsights = (() => {
    const filteredDashboards = filterDashboards(skillsDashboards);
    if (filteredDashboards?.length === 1 && filteredDashboards[0].sigmaKey) {
      return <SigmaDashboard assistantId={selectedAssistantId} dashboardKey={filteredDashboards[0].sigmaKey} />;
    }

    if (filteredDashboards?.length === 1) {
      return (
        <Box mt={3}>
          <InsightsBase
            key={selectedAssistantId}
            embedUrl={
              isLoggedInToLooker
                ? skillsDashboards[0].nonLoginEmbedUrl
                : skillsDashboards[0].embeddedDashboardData.embedUrl
            }
          />
        </Box>
      );
    }

    return <HyroTabs tabsSpec={getTabsSpec(filteredDashboards, selectedAssistantId)} selectedValue={searchParams.get("dashboard")} />;
  })();

  if (skillsDashboards === null) {
    return <HyroLoader {...{ text: INSIGHTS_LOADER_TEXT }} />;
  }
  return skillsDashboards?.length > 0 ? renderInsights : <NothingHereYet />;

};

export default AssistantInsights;
